import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Send, Trash2, Bot, AlertTriangle, Loader2, ChevronRight } from 'lucide-react';
import type { DraftDiagramOut } from '@/services/draftDiagramService';

interface DraftDiagramCardProps {
  draft: DraftDiagramOut;
  canEdit: boolean;
  onSelect: (draft: DraftDiagramOut) => void;
  onImport: (id: number) => void;
  onDiscard: (id: number) => void;
  isImporting: boolean;
  importDisabledReason?: string | null;
}

export default function DraftDiagramCard({
  draft,
  canEdit,
  onSelect,
  onImport,
  onDiscard,
  isImporting,
  importDisabledReason = null,
}: DraftDiagramCardProps) {
  const payload = draft.payload;
  const entityCount = payload.entities?.length ?? 0;
  const pathCount = payload.paths?.length ?? 0;
  const boundaryCount = payload.boundaries?.length ?? 0;
  const hasUnresolved = pathCount > 0 && payload.paths?.some((p) => !p.source_id || !p.target_id);

  return (
    <div className="flex items-center justify-between rounded-lg border border-dashed border-blue-400 dark:border-blue-600 bg-card px-4 py-3 group">
      {/* Clickable area → opens detail view */}
      <button
        type="button"
        onClick={() => onSelect(draft)}
        className="flex-1 min-w-0 text-left"
      >
        <div className="flex items-center gap-2">
          <p className="font-medium text-sm truncate">{draft.name}</p>
          <span className="inline-flex items-center rounded-md bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10 dark:bg-blue-900/30 dark:text-blue-400 shrink-0">
            Draft
          </span>
          <span className="inline-flex items-center gap-0.5 rounded-md bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-inset ring-slate-600/10 dark:bg-slate-900/30 dark:text-slate-400 shrink-0">
            <Bot className="h-2.5 w-2.5" />
            Agent
          </span>
          {hasUnresolved && (
            <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-700/10 dark:bg-amber-900/30 dark:text-amber-400 shrink-0">
              <AlertTriangle className="h-2.5 w-2.5" />
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          {entityCount} entities · {pathCount} connections
          {boundaryCount > 0 && ` · ${boundaryCount} boundaries`}
        </p>
      </button>

      {/* Inline action buttons */}
      <div className="flex items-center gap-1 shrink-0 ml-2">
        {canEdit && (
          <>
            <Tooltip>
              <TooltipTrigger asChild>
                <span title={importDisabledReason ?? undefined} className="inline-flex">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    title={importDisabledReason ?? 'Import into ThreatCat'}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (importDisabledReason) return;
                      onImport(draft.id);
                    }}
                    disabled={isImporting || Boolean(importDisabledReason)}
                    aria-label="Import to ThreatCat"
                  >
                    {isImporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Send className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>{importDisabledReason ?? 'Import into ThreatCat'}</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-destructive hover:text-destructive"
                  onClick={(e) => { e.stopPropagation(); onDiscard(draft.id); }}
                  aria-label="Discard draft"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Discard</TooltipContent>
            </Tooltip>
          </>
        )}
        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
      </div>
    </div>
  );
}
